import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import Button, { ButtonTypes } from "../../../../components/Button";
import { ConfirmPopUp } from "../../../../components/PopUp";
import { soleListingOptionOf } from "./constants";
import { ListingPicker } from "./ListingPicker";
import {
  EditorActions,
  EditorError,
  EditorForm,
  FixSection,
  FixSectionTitle,
} from "./styles";
import { ListingRepairSectionProps, ListingRepairValues } from "./types";
import { useListingDiscovery } from "./useListingDiscovery";

export const ListingRepairSection: React.FC<ListingRepairSectionProps> = ({
  instrument,
  isSubmitting,
  error,
  draft,
  onDraftChange,
  onRepair,
}) => {
  const { symbol, exchange, yahooSymbol, manualPricingOnly } = instrument;
  const [pendingSymbol, setPendingSymbol] = useState<string | null>(null);
  const identity = { symbol, exchange };
  const discovery = useListingDiscovery(identity, draft?.discovery);
  const { control, handleSubmit, setValue } = useForm<ListingRepairValues>({
    defaultValues: draft?.values ?? { listing: null },
  });
  const listing = useWatch({ control, name: "listing" });
  // Only candidates are worth restoring; a failed or empty search is quicker to repeat than to guard.
  const keptDiscovery =
    discovery.candidates.length > 0 ? discovery.result : null;

  useEffect(() => {
    onDraftChange?.(
      listing || keptDiscovery
        ? { values: { listing }, discovery: keptDiscovery }
        : null,
    );
  }, [listing, keptDiscovery, onDraftChange]);

  const handleFind = async () => {
    setValue("listing", soleListingOptionOf(await discovery.find(identity)));
  };
  const handleFindClick = () => void handleFind();

  const replacesPricing = yahooSymbol !== null || manualPricingOnly;

  const submit = ({ listing: chosen }: ListingRepairValues) => {
    if (!chosen) {
      return;
    }
    if (replacesPricing) {
      setPendingSymbol(chosen.value);
    } else {
      onRepair(chosen.value);
    }
  };
  const handleConfirmOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setPendingSymbol(null);
    }
  };
  const handleConfirm = () => {
    if (pendingSymbol) {
      onRepair(pendingSymbol);
    }
    setPendingSymbol(null);
  };

  const canSave = Boolean(listing) && listing?.value !== yahooSymbol;
  const noMatchMessage = discovery.hasNoMatch
    ? `No listing found for ${symbol} on ${exchange}. Use manual pricing if it can't be priced.`
    : null;

  return (
    <FixSection aria-label="Yahoo listing">
      <FixSectionTitle>Yahoo listing</FixSectionTitle>
      <EditorForm noValidate onSubmit={handleSubmit(submit)}>
        <ListingPicker
          control={control}
          disabled={isSubmitting}
          candidates={discovery.candidates}
          noMatchMessage={noMatchMessage}
          discoveryError={discovery.error}
          isFinding={discovery.isFinding}
          onFind={handleFindClick}
        />
        {error && <EditorError role="alert">{error}</EditorError>}
        <EditorActions>
          <Button
            type={ButtonTypes.primary}
            buttonType="submit"
            disabled={!canSave}
            isLoading={isSubmitting}
          >
            Save listing
          </Button>
        </EditorActions>
      </EditorForm>
      <ConfirmPopUp
        isOpen={pendingSymbol !== null}
        setIsOpen={handleConfirmOpenChange}
        title={`Save this listing for ${symbol}?`}
        text={`${symbol} will be priced from ${pendingSymbol}. Its provider price history will be removed and fetched again, and every holder will be revalued.`}
        confirmLabel="Save listing"
        onConfirm={handleConfirm}
      />
    </FixSection>
  );
};
